/**
 * BonusCenterPage — Bonus & Empfehlungscenter
 *
 * Zwei unabhängige Mechanismen auf einer Seite:
 *
 * 1. Freunde werben  — Referral-Link teilen → Guthaben für Rechnungsabzug
 * 2. Förderfuchs-Bonus — Opt-in für 5 Extra-Posts/Monat gegen max.
 *    1 Markenerwähnung/Woche im Monatsplan
 *
 * Backend-Endpunkte:
 *   GET  /api/account/me
 *   GET  /api/referral/my
 *   POST /api/referral/generate-link
 *   PATCH /api/account/foerderfuchs-bonus
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Gift, Users, Copy, Check, ExternalLink, Loader2,
  AlertTriangle, Euro, ChevronRight, Sparkles, Info,
  ToggleLeft, ToggleRight, Star
} from "lucide-react";
import { useFeatureGate } from "../hooks/useFeatureGate";
import { company } from "../data/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Kleiner Hilfsbaustein: Info-Box ───────────────────────────────────────────
function InfoBox({ children, variant = "blue" }) {
  const styles = {
    blue:   "bg-sky-50 border-sky-200 text-sky-800",
    amber:  "bg-amber-50 border-amber-200 text-amber-800",
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    green:  "bg-green-50 border-green-200 text-green-800",
  };
  return (
    <div className={`flex items-start gap-2.5 border rounded-xl px-4 py-3 text-sm ${styles[variant]}`}>
      <Info size={15} className="flex-shrink-0 mt-0.5" />
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

// ── Sektion-Karte ──────────────────────────────────────────────────────────────
function SectionCard({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

// ── Referral-Sektion ───────────────────────────────────────────────────────────
function ReferralSection() {
  const [referral, setReferral]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied]       = useState(false);
  const [error, setError]         = useState(null);

  const loadReferral = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/referral/my`);
      setReferral(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReferral(); }, [loadReferral]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/referral/generate-link`);
      setReferral(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      setError("Link konnte nicht generiert werden.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!referral?.referral_link) return;
    navigator.clipboard.writeText(referral.referral_link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <SectionCard>
      {/* Header */}
      <div className="px-6 py-5 border-b border-stone-100 bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <Users size={20} className="text-orange-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-stone-900 text-lg">Freunde werben</h2>
            <p className="text-sm text-stone-500">Teile deinen Link — erhalte Guthaben für jede Empfehlung</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6 space-y-5">
        {/* Wie es funktioniert */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { step: "1", text: "Deinen Link teilen", icon: ExternalLink },
            { step: "2", text: "Freund registriert sich", icon: Users },
            { step: "3", text: "Du erhältst Guthaben", icon: Euro },
          ].map(({ step, text, icon: Icon }) => (
            <div key={step} className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
              <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                {step}
              </div>
              <Icon size={14} className="text-stone-400 flex-shrink-0" />
              <span className="text-xs text-stone-600 leading-snug">{text}</span>
            </div>
          ))}
        </div>

        <InfoBox variant="amber">
          Für jede Empfehlung, die zu einem zahlenden Kunden wird, erhältst du <strong>Guthaben</strong>,
          das automatisch mit deiner nächsten Rechnung verrechnet wird.
        </InfoBox>

        {/* Fehler */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle size={14} />
            {error}
          </div>
        )}

        {/* Lade-Zustand */}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-stone-400 py-2">
            <Loader2 size={16} className="animate-spin" />
            Daten werden geladen…
          </div>
        )}

        {/* Guthaben-Badge */}
        {!loading && referral && (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Euro size={16} className="text-green-600" />
              <div>
                <p className="text-xs text-green-600 font-medium">Aktuelles Guthaben</p>
                <p className="font-bold text-green-800 text-lg">
                  {referral.credit_euros?.toFixed(2) ?? "0,00"} €
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
              <Users size={16} className="text-stone-500" />
              <div>
                <p className="text-xs text-stone-500 font-medium">Erfolgreiche Empfehlungen</p>
                <p className="font-bold text-stone-800 text-lg">
                  {referral.paid_referrals ?? 0}
                  <span className="text-stone-400 font-normal text-sm ml-1">
                    / {referral.total_referrals ?? 0} gesamt
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Referral-Link */}
        {!loading && referral?.referral_link ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
              Dein persönlicher Empfehlungslink
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-700 font-mono truncate select-all">
                {referral.referral_link}
              </div>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-green-100 text-green-700"
                    : "bg-orange-500 text-white hover:bg-orange-600"
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Kopiert!" : "Kopieren"}
              </button>
            </div>
            <p className="text-[11px] text-stone-400">
              Code: <span className="font-mono font-medium">{referral.referral_code}</span>
            </p>
          </div>
        ) : !loading && !error ? (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
            Referral-Link generieren
          </button>
        ) : null}
      </div>
    </SectionCard>
  );
}

// ── Förderfuchs-Bonus-Sektion ──────────────────────────────────────────────────
function FoerderfuchsBonusSection() {
  const [enabled, setEnabled]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState(null);
  const [error, setError]       = useState(null);

  // Account-Daten laden
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/account/me`);
        setEnabled(res.data.foerderfuchs_bonus_enabled ?? false);
      } catch (err) {
        setError("Kontoeinstellungen konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async () => {
    if (saving) return;
    const next = !enabled;
    setSaving(true);
    setSaveMsg(null);
    setError(null);
    try {
      const res = await axios.patch(`${API}/account/foerderfuchs-bonus`, { enabled: next });
      setEnabled(next);
      setSaveMsg(res.data.message);
      setTimeout(() => setSaveMsg(null), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || "Einstellung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard>
      {/* Header */}
      <div className="px-6 py-5 border-b border-stone-100 bg-gradient-to-r from-violet-50 to-purple-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <Sparkles size={20} className="text-violet-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading font-bold text-stone-900 text-lg">Förderfuchs-Bonus</h2>
              <span className="text-[10px] font-bold px-2 py-0.5 bg-violet-200 text-violet-800 rounded-full">
                5 EXTRA-POSTS
              </span>
            </div>
            <p className="text-sm text-stone-500">5 kostenlose Bonus-Posts/Monat im Tausch gegen eine Markenerwähnung/Woche</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6 space-y-5">
        {/* Was bekommst du */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 space-y-1.5">
            <div className="flex items-center gap-2 text-violet-700 font-semibold text-sm">
              <Star size={14} />
              Du bekommst
            </div>
            <ul className="space-y-1">
              {[
                "5 Extra-Posts pro Monat",
                "Fertige Entwürfe im Kalender",
                "Kein Mehraufwand für dich",
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-violet-800">
                  <Check size={11} className="text-violet-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-1.5">
            <div className="flex items-center gap-2 text-stone-600 font-semibold text-sm">
              <ChevronRight size={14} />
              Im Gegenzug
            </div>
            <ul className="space-y-1">
              {[
                "Max. 1 Post/Woche erwähnt Förderfuchs",
                "Selbst formuliert & authentisch",
                "Jederzeit deaktivierbar",
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-stone-600">
                  <Check size={11} className="text-stone-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <InfoBox variant="violet">
          Die Bonus-Posts sind einfache, authentische Erwähnungen — kein Werbesprech.
          Du kannst den Text vor der Veröffentlichung wie jeden anderen Post bearbeiten.
          Der Bonus wird beim nächsten Monatsplan automatisch eingeplant.
        </InfoBox>

        {/* Fehler / Erfolg */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle size={14} />
            {error}
          </div>
        )}
        {saveMsg && !error && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <Check size={14} />
            {saveMsg}
          </div>
        )}

        {/* Toggle */}
        <div className="flex items-center justify-between py-4 border-t border-stone-100">
          <div className="space-y-0.5">
            <p className="font-semibold text-stone-800 text-sm">
              {loading ? "Wird geladen…" : enabled ? "Bonus aktiv" : "Bonus deaktiviert"}
            </p>
            <p className="text-xs text-stone-500">
              {enabled
                ? "5 Extra-Posts werden beim nächsten Monatsplan eingeplant."
                : "Aktiviere den Bonus, um 5 Extra-Posts zu erhalten."}
            </p>
          </div>

          <button
            onClick={handleToggle}
            disabled={loading || saving}
            aria-pressed={enabled}
            className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              loading || saving
                ? "opacity-50 cursor-not-allowed bg-stone-100 text-stone-400"
                : enabled
                  ? "bg-violet-600 text-white hover:bg-violet-700"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : enabled ? (
              <ToggleRight size={20} />
            ) : (
              <ToggleLeft size={20} />
            )}
            {enabled ? "Aktiv" : "Aktivieren"}
          </button>
        </div>

        {/* Deaktivierungs-Hinweis */}
        {enabled && (
          <p className="text-[11px] text-stone-400 leading-relaxed">
            Du kannst den Bonus jederzeit deaktivieren. Bereits eingeplante Bonus-Posts
            bleiben im Kalender und können dort manuell bearbeitet oder gelöscht werden.
          </p>
        )}
      </div>
    </SectionCard>
  );
}

// ── Hauptseite ─────────────────────────────────────────────────────────────────
export default function BonusCenterPage() {
  const gate = useFeatureGate("bonus_center");

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Seiten-Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-violet-500 rounded-xl flex items-center justify-center">
            <Gift size={20} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-stone-900">Bonus & Empfehlungen</h1>
            <p className="text-sm text-stone-500">Wachse mit Förderfuchs — und profitiere davon.</p>
          </div>
        </div>
      </div>

      {/* Feature-Gate: nur wenn nicht freigeschaltet, Hinweis zeigen */}
      {!gate.allowed && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {gate.upgradeHint ?? "Diese Funktionen sind in deinem aktuellen Plan verfügbar."}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Trotzdem kannst du alle Funktionen auf dieser Seite bereits ausprobieren.
            </p>
          </div>
        </div>
      )}

      {/* Sektion 1: Freunde werben */}
      <ReferralSection />

      {/* Sektion 2: Förderfuchs-Bonus */}
      <FoerderfuchsBonusSection />

      {/* Footer-Hinweis */}
      <p className="text-xs text-stone-400 text-center pb-4">
        Fragen zu Guthaben oder Bonus? Schreib uns unter{" "}
        <a href={`mailto:${company.email}`} className="underline hover:text-stone-600">
          {company.email}
        </a>
      </p>
    </div>
  );
}
